import { FetchProps } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { TFunction } from 'i18next'
import { QueryClient } from '@tanstack/react-query'

type AddWallboxPricesProps = {
  payload: Array<{ from: number; price: number }>
  authFetch: <T>(props: FetchProps) => Promise<T>
  enqueueSuccessStack: (message: string) => void
  enqueueErrorStack: (message: string, errorCode?: string | number | undefined, errorMessage?: string | undefined) => void
  setIsLoading: (value: boolean) => void
  onClose: () => void
  t: TFunction
  queryClient: QueryClient
  priceType: 'charge' | 'discharge'
  serialNo: string
  wallboxIndex: number
}

/**
 * Function for adding or editing prices per single wallbox.
 * It can add prices for both charge or discharge, depends on energy direction
 * @param payload prices with array of from and price
 * @param setIsLoading loading state
 * @param onClose close modal function
 * @param priceType corresponds to charge or discharge direction
 * @param serialNo installation serial no
 * @param wallboxIndex wallbox number for endpoint param
 * @param authFetch authentication function from useAuthFetchWithResult,
 * @param t translation from useCustomTranslation,
 * @param queryClient query client from QueryClient for invalidate tags
 * @param enqueueErrorStack error stack from useSnackbar,
 * @param enqueueSuccessStack success stack from useSnackbar,
 * @return Promise void
 */
export const addWallboxPrices = async ({
  payload,
  priceType,
  setIsLoading,
  onClose,
  authFetch,
  t,
  enqueueErrorStack,
  enqueueSuccessStack,
  queryClient,
  serialNo,
  wallboxIndex,
}: AddWallboxPricesProps): Promise<void> => {
  const isChargePriceType = priceType === 'charge'

  try {
    setIsLoading(true)
    await authFetch({
      endpoint: process.env.REACT_APP_EMOBILITY_ENDPOINT,
      path: `wallboxes/${serialNo}/${wallboxIndex}/prices/${priceType}`,
      method: 'POST',
      payload: { prices: payload },
    })
    queryClient.invalidateQueries({ queryKey: isChargePriceType ? ['wallboxChargePrices'] : ['wallboxDischargePrices'] })
    enqueueSuccessStack(
      isChargePriceType ? t('emobility.wallbox.editTariffs.chargePriceSuccess') : t('emobility.wallbox.editTariffs.dischargePriceSuccess')
    )
    onClose()
  } catch (error) {
    enqueueErrorStack(
      isChargePriceType ? t('emobility.wallbox.editTariffs.chargePriceError') : t('emobility.wallbox.editTariffs.dischargePriceError')
    )
  } finally {
    setIsLoading(false)
  }
}
